import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Divider,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { makeStyles } from "@material-ui/styles";
import clsx from "clsx";
import _ from "lodash";
import PropTypes from "prop-types";
import React, { useState } from "react";
import { useAlert } from "react-alert";
import PerfectScrollbar from "react-perfect-scrollbar";

import useEnvironment from "./hooks/use-environment";

import Navigation from "./Navigation";
import { sources } from "./sources/sources";

function jsSnippet(currentEnvironment) {
  const fpJsPath = "/static/js/freshpaint.js";
  let { ID, apiHost, appHost } = currentEnvironment;
  if (appHost && appHost.indexOf("http") === -1) {
    appHost = `https://${appHost}`;
  }

  let initLine, defaultLibURL;
  if (_.isUndefined(apiHost)) {
    initLine = `freshpaint.init("${ID}");`;
    defaultLibURL = `//perfalytics.com${fpJsPath}`;
  } else {
    initLine = `freshpaint.init("${ID}", {"api_host": "${apiHost}", "app_host": "${appHost}"});`;
    defaultLibURL = `${appHost}${fpJsPath}`;
  }

  return String.raw`<script type="text/javascript">
(function(c,a){if(!a.__SV){var b=window;try{var d,m,j,k=b.location,f=k.hash;d=function(a,b){return(m=a.match(RegExp(b+"=([^&]*)")))?m[1]:null};f&&d(f,"fpState")&&(j=JSON.parse(decodeURIComponent(d(f,"fpState"))),"fpeditor"===j.action&&(b.sessionStorage.setItem("_fpcehash",f),history.replaceState(j.desiredHash||"",c.title,k.pathname+k.search)))}catch(n){}var l,h;window.freshpaint=a;a._i=[];a.init=function(b,d,g){function c(b,i){var a=i.split(".");2==a.length&&(b=b[a[0]],i=a[1]);b[i]=function(){b.push([i].concat(Array.prototype.slice.call(arguments,
0)))}}var e=a;"undefined"!==typeof g?e=a[g]=[]:g="freshpaint";e.people=e.people||[];e.toString=function(b){var a="freshpaint";"freshpaint"!==g&&(a+="."+g);b||(a+=" (stub)");return a};e.people.toString=function(){return e.toString(1)+".people (stub)"};l="disable time_event track track_pageview track_links track_forms track_with_groups add_group set_group remove_group register register_once alias unregister identify name_tag set_config reset opt_in_tracking opt_out_tracking has_opted_in_tracking has_opted_out_tracking clear_opt_in_out_tracking people.set people.set_once people.unset people.increment people.append people.union people.track_charge people.clear_charges people.delete_user people.remove people group page alias ready addEventProperties addInitialEventProperties removeEventProperty addPageviewProperties".split(" ");
for(h=0;h<l.length;h++)c(e,l[h]);var f="set set_once union unset remove delete".split(" ");e.get_group=function(){function a(c){b[c]=function(){call2_args=arguments;call2=[c].concat(Array.prototype.slice.call(call2_args,0));e.push([d,call2])}}for(var b={},d=["get_group"].concat(Array.prototype.slice.call(arguments,0)),c=0;c<f.length;c++)a(f[c]);return b};a._i.push([b,d,g])};a.__SV=1.4;b=c.createElement("script");b.type="text/javascript";b.async=!0;b.src="undefined"!==typeof FRESHPAINT_CUSTOM_LIB_URL?
FRESHPAINT_CUSTOM_LIB_URL:"${defaultLibURL}";(d=c.getElementsByTagName("script")[0])?d.parentNode.insertBefore(b,d):c.head.appendChild(b)}})(document,window.freshpaint||[]);
${initLine}
freshpaint.page();
</script>`;
}

const useTableStyles = makeStyles((theme) => ({
  content: {
    padding: 0,
    "&:last-child": {
      paddingBottom: 0,
    },
  },
  inner: {
    minWidth: 700,
  },
  switchContainerHidden: {
    visibility: "hidden",
  },
  card: {
    width: "65%",
    minWidth: 800,
  },
  clickableImg: {
    maxHeight: 40,
    "&:hover": {
      cursor: "pointer",
    },
  },
  navigation: {
    paddingTop: 24,
  },
  sourceInfo: {
    display: "flex",
    alignItems: "center",
  },
  sourceName: {
    color: "#444",
    fontSize: 16,
    verticalAlign: "center",
    marginLeft: theme.spacing(2),
  },
  tableRow: {
    "&:hover": {
      backgroundColor: "#f0f0f0",
      cursor: "pointer",
      "& button": {
        backgroundColor: "rgba(63, 81, 181, 0.08)",
      },
    },
  },
  tableCell: {
    borderBottom: "none",
    alignItems: "center",
  },
}));

function SourcesPage() {
  const classes = useTableStyles();

  const currentEnvironment = useEnvironment();
  const [configureModalSource, setConfigureModalSource] = useState(null);

  return (
    <Navigation className={clsx(classes.navigation)}>
      <Typography component="h2" gutterBottom variant="overline">
        Sources
      </Typography>
      <Typography component="h1" gutterBottom variant="h3">
        Collect data from your website or app
      </Typography>

      <Card className={clsx(classes.card)}>
        <CardHeader title="Sources" />
        <Divider />
        <CardContent className={clsx(classes.content)}>
          <PerfectScrollbar>
            <div className={clsx(classes.inner)}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Integration</TableCell>
                    <TableCell align="center">Configure</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sources.map((source) => {
                    const SourceIcon = source.logoSquare;

                    return (
                      <TableRow
                        className={classes.tableRow}
                        key={source.key}
                        onClick={() => setConfigureModalSource(source)}
                      >
                        <TableCell className={classes.tableCell}>
                          <div className={classes.sourceInfo}>
                            {SourceIcon && (
                              <SourceIcon height={42} width={42} />
                            )}
                            <span className={classes.sourceName}>
                              {source.name}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell align="center" className={classes.tableCell}>
                          <Button
                            variant="outlined"
                            color="primary"
                            onClick={() => setConfigureModalSource(source)}
                          >
                            Configure
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </PerfectScrollbar>
        </CardContent>
      </Card>
      <ConfigureSourceModal
        currentEnvironment={currentEnvironment}
        open={configureModalSource !== null}
        source={configureModalSource}
        onClose={() => setConfigureModalSource(null)}
      />
    </Navigation>
  );
}

const useModalStyles = makeStyles((theme) => ({
  root: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    outline: "none",
    boxShadow: theme.shadows[20],
    width: 500,
    maxHeight: "100%",
    overflowY: "auto",
    maxWidth: "100%",
  },
  container: {
    marginTop: theme.spacing(3),
    height: 200,
  },
  actions: {
    justifyContent: "flex-end",
  },
  inputField: {
    marginTop: 16,
    overflowX: "wrap",
    wordBreak: "break-all",
  },
  deactivateButton: {
    backgroundColor: "#B71C1C",
    color: "white",
  },
  closeIconAction: {
    "&:hover": {
      cursor: "pointer",
    },
    margin: 0,
  },
}));

function ConfigureSourceModal({ source, open, onClose, currentEnvironment }) {
  if (source === null) {
    return null;
  } else if (source.modal === "Autotrack") {
    return (
      <JavascriptModal
        open={open}
        onClose={onClose}
        currentEnvironment={currentEnvironment}
        title={"Configure Autotrack"}
        body={
          "Add the Freshpaint Javascript SDK to your site under the head tag. The Autotrack source will automatically start collecting data from your site."
        }
      />
    );
  } else if (source.modal === "Precision Tracking") {
    return (
      <JavascriptModal
        open={open}
        onClose={onClose}
        currentEnvironment={currentEnvironment}
        title={"Configure Precision Tracking"}
        body={
          <>
            Add the Freshpaint Javascript SDK to your site under the head tag.
            See the{" "}
            <a
              className="text-brand-red underline"
              href="https://docs.freshpaint.io/data-management/sources/precision-tracking"
              rel="noopener noreferrer"
            >
              Precision Tracking docs
            </a>{" "}
            to see how to setup Precision Tracking.
          </>
        }
      />
    );
  } else if (source.modal === "Server Side") {
    return (
      <ServerSideModal
        source={source}
        open={open}
        onClose={onClose}
        currentEnvironment={currentEnvironment}
      />
    );
  } else if (source.modal === "React Native") {
    return (
      <ReactNativeModal
        source={source}
        open={open}
        onClose={onClose}
        currentEnvironment={currentEnvironment}
      />
    );
  } else if (source.modal === "Android") {
    return (
      <AndroidModal
        source={source}
        open={open}
        onClose={onClose}
        currentEnvironment={currentEnvironment}
      />
    );
  } else if (source.modal === "iOS") {
    return (
      <IOSModal
        source={source}
        open={open}
        onClose={onClose}
        currentEnvironment={currentEnvironment}
      />
    );
  } else if (source.modal === "Sendinblue") {
    return (
      <SendinblueModal
        source={source}
        open={open}
        onClose={onClose}
        currentEnvironment={currentEnvironment}
      />
    );
  } else if (source.modal === "Mailchimp") {
    return (
      <MailchimpModal
        source={source}
        open={open}
        onClose={onClose}
        currentEnvironment={currentEnvironment}
      />
    );
  } else if (source.modal === "Intercom") {
    return (
      <IntercomModal
        source={source}
        open={open}
        onClose={onClose}
        currentEnvironment={currentEnvironment}
      />
    );
  } else if (source.modal === "Contact Us") {
    return <ContactUsModal source={source} open={open} onClose={onClose} />;
  }

  return null;
}

ConfigureSourceModal.propTypes = {
  source: PropTypes.object,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  currentEnvironment: PropTypes.object.isRequired,
};

function JavascriptModal({ open, onClose, currentEnvironment, title, body }) {
  const alert = useAlert();
  const classes = useModalStyles();

  function handleCopySnippet() {
    const copyText = document.getElementById("freshpaint-javascript-sdk");
    copyText.select();
    copyText.setSelectionRange(0, 99999); /*For mobile devices*/
    document.execCommand("copy");
    alert.success("Copied Code");
  }

  return (
    <Modal onClose={onClose} open={open}>
      <Card className={clsx(classes.root)}>
        <CardHeader
          title={title}
          classes={{ action: clsx(classes.closeIconAction) }}
          action={<CloseIcon onClick={onClose} />}
        />
        <Divider />
        <CardContent>
          <Typography variant="body1">{body}</Typography>
          <TextField
            fullWidth
            id="freshpaint-javascript-sdk"
            inputProps={{ readOnly: true }}
            multiline={true}
            rows={4}
            label="Frsehpaint Javascript SDK"
            name="freshpaint-javascript-sdk"
            value={jsSnippet(currentEnvironment)}
            variant="outlined"
            className={clsx(classes.inputField)}
          />
        </CardContent>
        <Divider />
        <CardActions className={classes.actions}>
          <Button
            color="primary"
            onClick={() => handleCopySnippet()}
            variant="contained"
          >
            Copy Snippet
          </Button>
        </CardActions>
      </Card>
    </Modal>
  );
}

JavascriptModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  currentEnvironment: PropTypes.object.isRequired,
  title: PropTypes.string.isRequired,
  body: PropTypes.string.isRequired,
};

function ServerSideModal({ open, onClose, currentEnvironment }) {
  const classes = useModalStyles();

  return (
    <Modal onClose={onClose} open={open}>
      <Card className={clsx(classes.root)}>
        <CardHeader
          title={`Tracking server-side events`}
          classes={{ action: clsx(classes.closeIconAction) }}
          action={<CloseIcon onClick={onClose} />}
        />
        <Divider />
        <CardContent>
          <Typography variant="body1">
            Server-side events can be tracked by using our HTTP API with your
            environment ID: <code>{currentEnvironment.ID}</code>
          </Typography>
          <br />
          <Typography variant="body1">
            See the{" "}
            <a
              className="text-brand-red underline"
              href="https://docs.freshpaint.io/data-management/sources/server-side"
              target="_blank"
              rel="noopener noreferrer"
            >
              server-side docs to get started.
            </a>
          </Typography>
        </CardContent>
        <Divider />
        <CardActions className={classes.actions}>
          <Button
            color="primary"
            href="https://docs.freshpaint.io/data-management/sources/server-side"
            target="_blank"
            rel="noopener noreferrer"
            variant="contained"
          >
            READ THE DOCS
          </Button>
        </CardActions>
      </Card>
    </Modal>
  );
}

ServerSideModal.propTypes = {
  source: PropTypes.object,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  currentEnvironment: PropTypes.object.isRequired,
};

function ReactNativeModal({ open, onClose, currentEnvironment }) {
  const classes = useModalStyles();

  return (
    <Modal onClose={onClose} open={open}>
      <Card className={clsx(classes.root)}>
        <CardHeader
          title={`Track React Native events`}
          classes={{ action: clsx(classes.closeIconAction) }}
          action={<CloseIcon onClick={onClose} />}
        />
        <Divider />
        <CardContent>
          <Typography variant="body1">
            You can track React Native events with the Freshpaint React Native
            SDK. The SDK will autocapture touches, edits, and screen views.
          </Typography>
          <br />
          <Typography variant="body1">
            Your environment ID is: <code>{currentEnvironment.ID}</code>
          </Typography>
          <br />
          <Typography variant="body1">
            See the{" "}
            <a
              className="text-brand-red underline"
              href="https://docs.freshpaint.io/getting-started/react-native-quickstart-guide"
            >
              React Native quickstart guide
            </a>{" "}
            to get started.
          </Typography>
        </CardContent>
        <Divider />
        <CardActions className={classes.actions}>
          <Button
            color="primary"
            onClick={() =>
              (window.location.href =
                "https://docs.freshpaint.io/getting-started/react-native-quickstart-guide")
            }
            variant="contained"
          >
            READ THE QUICKSTART GUIDE
          </Button>
        </CardActions>
      </Card>
    </Modal>
  );
}

ReactNativeModal.propTypes = {
  source: PropTypes.object,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  currentEnvironment: PropTypes.object.isRequired,
};

function AndroidModal({ open, onClose, currentEnvironment }) {
  const classes = useModalStyles();

  return (
    <Modal onClose={onClose} open={open}>
      <Card className={clsx(classes.root)}>
        <CardHeader
          title={`Track Android events`}
          classes={{ action: clsx(classes.closeIconAction) }}
          action={<CloseIcon onClick={onClose} />}
        />
        <Divider />
        <CardContent>
          <Typography variant="body1">
            You can track Android events with the Freshpaint Android SDK. Your
            environment ID is: <code>{currentEnvironment.ID}</code>
          </Typography>
          <br />
          <Typography variant="body1">
            See the{" "}
            <a
              className="text-brand-red underline"
              href="https://docs.freshpaint.io/getting-started/android-quickstart-guide"
            >
              Android quickstart guide
            </a>{" "}
            to get started.
          </Typography>
        </CardContent>
        <Divider />
        <CardActions className={classes.actions}>
          <Button
            color="primary"
            onClick={() =>
              (window.location.href =
                "https://docs.freshpaint.io/getting-started/android-quickstart-guide")
            }
            variant="contained"
          >
            READ THE QUICKSTART GUIDE
          </Button>
        </CardActions>
      </Card>
    </Modal>
  );
}

function IOSModal({ open, onClose, currentEnvironment }) {
  const classes = useModalStyles();

  return (
    <Modal onClose={onClose} open={open}>
      <Card className={clsx(classes.root)}>
        <CardHeader
          title={`Track iOS events`}
          classes={{ action: clsx(classes.closeIconAction) }}
          action={<CloseIcon onClick={onClose} />}
        />
        <Divider />
        <CardContent>
          <Typography variant="body1">
            You can track iOS events with the Freshpaint iOS SDK. Your
            environment ID is: <code>{currentEnvironment.ID}</code>
          </Typography>
          <br />
          <Typography variant="body1">
            See the{" "}
            <a
              className="text-brand-red underline"
              href="https://docs.freshpaint.io/getting-started/ios-quickstart-guide"
            >
              iOS quickstart guide
            </a>{" "}
            to get started.
          </Typography>
        </CardContent>
        <Divider />
        <CardActions className={classes.actions}>
          <Button
            color="primary"
            onClick={() =>
              (window.location.href =
                "https://docs.freshpaint.io/getting-started/ios-quickstart-guide")
            }
            variant="contained"
          >
            READ THE QUICKSTART GUIDE
          </Button>
        </CardActions>
      </Card>
    </Modal>
  );
}

function SendinblueModal({ open, onClose, currentEnvironment }) {
  const classes = useModalStyles();

  return (
    <Modal onClose={onClose} open={open}>
      <Card className={clsx(classes.root)}>
        <CardHeader
          title={`Tracking Sendinblue email activity`}
          classes={{ action: clsx(classes.closeIconAction) }}
          action={<CloseIcon onClick={onClose} />}
        />
        <Divider />
        <CardContent>
          <Typography variant="body1">
            Sendinblue email activity can be tracked setting up a Sendinblue
            webhook with the URL for your environment:
          </Typography>
          <TextField
            fullWidth
            id="freshpaint-sendinblue-webhook"
            inputProps={{ readOnly: true }}
            multiline={true}
            rows={2}
            label="Sendinblue webhook URL"
            name="freshpaint-sendinblue-webhook"
            value={`https://api.perfalytics.com/sendinblue/${currentEnvironment.ID}`}
            variant="outlined"
            className={clsx(classes.inputField)}
          />
          <br />
          <Typography variant="body1">
            See the{" "}
            <a
              className="text-brand-red underline"
              href="https://docs.freshpaint.io/data-management/sources/sendinblue"
              target="_blank"
              rel="noopener noreferrer"
            >
              Sendinblue docs to get started.
            </a>
          </Typography>
        </CardContent>
        <Divider />
        <CardActions className={classes.actions}>
          <Button
            color="primary"
            href="https://docs.freshpaint.io/data-management/sources/sendinblue"
            target="_blank"
            rel="noopener noreferrer"
            variant="contained"
          >
            READ THE DOCS
          </Button>
        </CardActions>
      </Card>
    </Modal>
  );
}

function MailchimpModal({ open, onClose, currentEnvironment }) {
  const classes = useModalStyles();

  return (
    <Modal onClose={onClose} open={open}>
      <Card className={clsx(classes.root)}>
        <CardHeader
          title={`Tracking Mailchimp email activity`}
          classes={{ action: clsx(classes.closeIconAction) }}
          action={<CloseIcon onClick={onClose} />}
        />
        <Divider />
        <CardContent>
          <Typography variant="body1">
            Mailchimp email activity can be tracked setting up a Mailchimp
            webhook with the URL for your environment:
          </Typography>
          <TextField
            fullWidth
            id="freshpaint-mailchimp-webhook"
            inputProps={{ readOnly: true }}
            multiline={true}
            rows={2}
            label="Mailchimp webhook URL"
            name="freshpaint-mailchimp-webhook"
            value={`https://api.perfalytics.com/mailchimp/${currentEnvironment.ID}`}
            variant="outlined"
            className={clsx(classes.inputField)}
          />
          <br />
          <Typography variant="body1">
            See the{" "}
            <a
              className="text-brand-red underline"
              href="https://docs.freshpaint.io/data-management/sources/mailchimp"
              target="_blank"
              rel="noopener noreferrer"
            >
              Mailchimp docs to get started.
            </a>
          </Typography>
        </CardContent>
        <Divider />
        <CardActions className={classes.actions}>
          <Button
            color="primary"
            href="https://docs.freshpaint.io/data-management/sources/mailchimp"
            target="_blank"
            rel="noopener noreferrer"
            variant="contained"
          >
            READ THE DOCS
          </Button>
        </CardActions>
      </Card>
    </Modal>
  );
}

function IntercomModal({ open, onClose, currentEnvironment }) {
  const classes = useModalStyles();

  return (
    <Modal onClose={onClose} open={open}>
      <Card className={clsx(classes.root)}>
        <CardHeader
          title={`Tracking Intercom activity`}
          classes={{ action: clsx(classes.closeIconAction) }}
          action={<CloseIcon onClick={onClose} />}
        />
        <Divider />
        <CardContent>
          <Typography variant="body1">
            Intercom activity can be tracked setting up an Intercom app with the
            webhook for your environment:
          </Typography>
          <TextField
            fullWidth
            id="freshpaint-intercom-webhook"
            inputProps={{ readOnly: true }}
            multiline={true}
            rows={2}
            label="Intercom webhook URL"
            name="freshpaint-intercom-webhook"
            value={`https://api.perfalytics.com/webhooks/intercom/${currentEnvironment.ID}`}
            variant="outlined"
            className={clsx(classes.inputField)}
          />
          <br />
          <Typography variant="body1">
            See the{" "}
            <a
              className="text-brand-red underline"
              href="https://docs.freshpaint.io/data-management/sources/intercom"
              target="_blank"
              rel="noopener noreferrer"
            >
              Intercom docs to get started.
            </a>
          </Typography>
        </CardContent>
        <Divider />
        <CardActions className={classes.actions}>
          <Button
            color="primary"
            href="https://docs.freshpaint.io/data-management/sources/intercom"
            target="_blank"
            rel="noopener noreferrer"
            variant="contained"
          >
            READ THE DOCS
          </Button>
        </CardActions>
      </Card>
    </Modal>
  );
}

ReactNativeModal.propTypes = MailchimpModal.propTypes = {
  source: PropTypes.object,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  currentEnvironment: PropTypes.object.isRequired,
};

function ContactUsModal({ source, open, onClose }) {
  const classes = useModalStyles();

  return (
    <Modal onClose={onClose} open={open}>
      <Card className={clsx(classes.root)}>
        <CardHeader
          title={`Import ${source.name} data into your Warehouse`}
          classes={{ action: clsx(classes.closeIconAction) }}
          action={<CloseIcon onClick={onClose} />}
        />
        <Divider />
        <CardContent>
          <Typography variant="body1">{source.body}</Typography>
          <br />
          <Typography variant="body1">
            Please contact us to setup this source.
          </Typography>
        </CardContent>
        <Divider />
        <CardActions className={classes.actions}>
          <Button
            color="primary"
            variant="contained"
            href="mailto:support@freshpaint.io"
            target="_blank"
            rel="noopener noreferrer"
          >
            Contact Us
          </Button>
        </CardActions>
      </Card>
    </Modal>
  );
}

ContactUsModal.propTypes = {
  source: PropTypes.object,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  currentEnvironment: PropTypes.string.isRequired,
};

export default SourcesPage;
